import React from 'react';
import { ISSUE_STATUS_LABELS, type Issue } from '../../types/issue';
import { stageVisual, workspaceStages } from '../../utils/issueProgress';
import { presentStageLabel } from '../../utils/structuredStageLabel';
import type { EnterpriseAction } from '../../services/actionService';

interface ProblemStageProgressBarProps {
  issue: Issue;
  actions: EnterpriseAction[];
  selectedKey?: string | null;
  onSelect?: (key: string) => void;
}

export const ProblemStageProgressBar: React.FC<ProblemStageProgressBarProps> = ({
  issue,
  actions,
  selectedKey,
  onSelect,
}) => {
  const stages = workspaceStages(issue);
  const activeIdx = stages.findIndex((stage) => stage.statuses.includes(issue.status));
  const offPath = activeIdx < 0 && issue.status !== 'closed' && issue.status !== 'cancelled';

  return (
    <div className="border-b border-slate-200 py-1.5 dark:border-slate-800">
      <nav aria-label="Etapas do problema" className="flex w-full items-start overflow-x-auto">
        {stages.map((stage, idx) => {
          const visual = stageVisual(issue, actions, stage.key, idx, activeIdx);
          const label = presentStageLabel(issue.mode, stage.key, stage.label);
          const selected = selectedKey === stage.key;
          const isCurrent = idx === activeIdx;
          return (
            <React.Fragment key={stage.key}>
              <button
                type="button"
                aria-current={isCurrent ? 'step' : undefined}
                aria-pressed={selected}
                onClick={() => onSelect?.(stage.key)}
                title={label}
                className={`flex shrink-0 cursor-pointer flex-col items-center gap-0.5 rounded px-1 py-0.5 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-slate-400 ${
                  selected ? 'bg-slate-100 ring-1 ring-slate-200 dark:bg-slate-800 dark:ring-slate-700' : 'hover:bg-slate-50 dark:hover:bg-slate-800/70'
                }`}
              >
                <span className={`flex h-5 w-5 items-center justify-center rounded-full text-[9px] font-bold ${
                  visual === 'complete'
                    ? 'bg-emerald-600 text-white'
                    : visual === 'current'
                      ? 'bg-blue-600 text-white ring-2 ring-blue-200 dark:ring-blue-900'
                      : visual === 'traversed'
                        ? 'bg-amber-100 text-amber-800 ring-1 ring-amber-300 dark:bg-amber-950 dark:text-amber-300 dark:ring-amber-800'
                        : 'bg-slate-200 text-slate-500 dark:bg-slate-700'
                }`}
                >
                  {visual === 'complete' ? '✓' : idx + 1}
                </span>
                <span className={`whitespace-nowrap text-center text-[10px] leading-tight ${
                  visual === 'current'
                    ? 'font-semibold text-blue-700 dark:text-blue-400'
                    : visual === 'complete'
                      ? 'font-medium text-emerald-700 dark:text-emerald-400'
                      : visual === 'traversed'
                        ? 'font-medium text-amber-700 dark:text-amber-400'
                        : 'text-slate-400'
                }`}
                >
                  {label}
                </span>
              </button>
              {idx < stages.length - 1 ? (
                <div className={`mt-[9px] h-px min-w-3 flex-1 ${visual === 'complete' ? 'bg-emerald-500' : 'bg-slate-200 dark:bg-slate-700'}`} />
              ) : null}
            </React.Fragment>
          );
        })}
      </nav>
      {offPath ? (
        <p className="mt-1 text-[11px] text-slate-500">
          Fluxo legado · {ISSUE_STATUS_LABELS[issue.status] ?? issue.status}
        </p>
      ) : null}
    </div>
  );
};

export default ProblemStageProgressBar;
